import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import CoverPage from './CoverPage';
import IntroPage from './IntroPage';
import PhotoGridPage from './PhotoGridPage';
import BriefPage from './BriefPage';
import FutureVisionPage from './FutureVisionPage';
import CollectiveStoryPage from './CollectiveStoryPage';
import ScenarioOverviewPage from './ScenarioOverviewPage';
import ScenarioCardsPage from './ScenarioCardsPage';
import FutureStoriesPage from './FutureStoriesPage';
import { DEMO_K3_CONTENT } from './demoContent.k3.js';

/**
 * PrintReport — Main renderer for the print-quality Kuutio report.
 *
 * Phase 0: Static K3 clone with hardcoded Finnish demo content
 * (see demoContent.k3.js). Chrome i18n only for toolbar; story bodies stay FI.
 * Route: /print-report
 */
export default function PrintReport() {
  const { t } = useTranslation();

  return (
    <ReportContainer>
      <style>{printStyles}</style>

      <ToolBar className="no-print">
        <ToolBarTitle>{t('printReport.toolbarTitle')}</ToolBarTitle>
        <DownloadButton onClick={() => window.print()}>
          {t('printReport.downloadPdf')}
        </DownloadButton>
      </ToolBar>

      <CoverPage
        instanceNumber={DEMO_K3_CONTENT.instanceNumber}
        title={DEMO_K3_CONTENT.coverTitle}
        subtitle={DEMO_K3_CONTENT.coverSubtitle}
      />

      <IntroPage />

      <PhotoGridPage />

      <BriefPage />

      <FutureVisionPage />

      <CollectiveStoryPage />

      <ScenarioOverviewPage />

      <ScenarioCardsPage />

      {DEMO_K3_CONTENT.futureStoriesPages.map((page, idx) => (
        <FutureStoriesPage
          key={idx}
          pageNumber={page.pageNumber}
          scenarioName={page.scenarioName}
          scenarioColor={page.scenarioColor}
          introText={page.introText}
          stories={page.stories}
        />
      ))}

      {/* Next pages will be added here one by one */}
    </ReportContainer>
  );
}


const ReportContainer = styled.div`
  background: #f0f0f0;

  @media screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 20px;

    .page {
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }
  }
`;

const ToolBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  width: 297mm;
  background: #1a1a1a;
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  border-radius: 8px;
  font-family: 'Rational', sans-serif;
`;

const ToolBarTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const DownloadButton = styled.button`
  background: #FBE300;
  color: #000;
  border: none;
  padding: 8px 24px;
  border-radius: 4px;
  font-family: 'Rational', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #e6cf00;
  }
`;

const printStyles = `
  @font-face {
    font-family: 'Rational';
    src: url('/fonts/Rational/RationalText-Book.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Rational';
    src: url('/fonts/Rational/RationalText-BookItalic.otf') format('opentype');
    font-weight: 400;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'Rational';
    src: url('/fonts/Rational/RationalText-Bold.woff2') format('woff2');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Rational';
    src: url('/fonts/Rational/RationalText-BoldItalic.otf') format('opentype');
    font-weight: 700;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'Rational';
    src: url('/fonts/Rational/RationalText-Black.otf') format('opentype');
    font-weight: 900;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Rational';
    src: url('/fonts/Rational/RationalText-Black.otf') format('opentype');
    font-weight: 900;
    font-style: italic;
    font-display: swap;
  }

  @media print {
    @page {
      size: 297mm 210mm;
      margin: 0;
    }

    body {
      margin: 0;
      padding: 0;
      background: white;
    }

    .page {
      width: 297mm;
      min-height: 210mm;
      page-break-after: always;
    }

    .no-print {
      display: none !important;
    }
  }
`;
